import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { queryClient, useTRPC } from '~/services/trpc'

import { toast } from '@hap/components'
import { MAX_FLAGS_PER_ORG } from '@hap/constants'

export namespace FlagMutators {
  interface MutableValues {
    name?: string
    color?: string
  }

  export function useCreate({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.flags.create.mutationOptions({
        onMutate: async (values) => {
          const queryKey = trpc.flags.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousFlags = queryClient.getQueriesData({ queryKey })

          // Optimistically add the new flag
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            const newFlag = {
              id: '__TEMP__',
              name: values.name,
              color: values.color,
              usage: 0,
              createdAt: new Date(),
              updatedAt: new Date(),
            }
            return old ? [...old, newFlag] : [newFlag]
          })

          return { previousFlags }
        },
        onSuccess: () => {
          toast.success('Flag created')
          queryClient.invalidateQueries({
            queryKey: trpc.flags.list.queryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.lookup.getFilterData.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to create flag')
          // Rollback optimistic update
          if (context?.previousFlags) {
            context.previousFlags.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const create = useCallback(
      async (values: { name: string; color: string }) => {
        const queryKey = trpc.flags.list.queryKey()
        const current = queryClient.getQueryData<any[]>(queryKey)
        if (current && current.length >= MAX_FLAGS_PER_ORG) {
          toast.error(
            `Maximum flags reached: an organization may have up to ${MAX_FLAGS_PER_ORG} flags`,
          )
          return
        }
        return mutateAsync(values)
      },
      [mutateAsync, trpc],
    )

    return { create, isPending }
  }

  export function useUpdate({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending, variables } = useMutation(
      trpc.flags.update.mutationOptions({
        onMutate: async (updates) => {
          const queryKey = trpc.flags.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousFlags = queryClient.getQueriesData({ queryKey })

          // Optimistically update the flag
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.map((flag) => {
              if (flag.id === updates.flagId) {
                return {
                  ...flag,
                  ...updates.values,
                  updatedAt: new Date(),
                }
              }
              return flag
            })
          })

          return { previousFlags }
        },
        onSuccess: () => {
          toast.success('Flag updated')
          queryClient.invalidateQueries({
            queryKey: trpc.flags.list.queryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.lookup.getFilterData.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to update flag')
          // Rollback optimistic update
          if (context?.previousFlags) {
            context.previousFlags.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const update = useCallback(
      async (values: { flagId: string; values: Partial<MutableValues> }) =>
        mutateAsync(values),
      [mutateAsync],
    )

    return { update, isPending, variables }
  }

  export function useDestroy({
    onSuccess,
  }: {
    onSuccess?: () => void
  } = {}) {
    const trpc = useTRPC()
    const { mutateAsync, isPending } = useMutation(
      trpc.flags.destroy.mutationOptions({
        onMutate: async (flagId) => {
          const queryKey = trpc.flags.list.queryKey()
          await queryClient.cancelQueries({ queryKey })
          const previousFlags = queryClient.getQueriesData({ queryKey })

          // Optimistically remove the flag
          queryClient.setQueriesData({ queryKey }, (old?: any[]) => {
            if (!old) return old
            return old.filter((flag) => flag.id !== flagId)
          })

          return { previousFlags }
        },
        onSuccess: () => {
          toast.success('Flag deleted')
          queryClient.invalidateQueries({
            queryKey: trpc.flags.list.queryKey(),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.lookup.getFilterData.queryKey(),
          })
          if (onSuccess) onSuccess()
        },
        onError: (err, _, context) => {
          console.error(err)
          toast.error('Failed to delete flag')
          // Rollback optimistic update
          if (context?.previousFlags) {
            context.previousFlags.forEach(([key, data]) => {
              queryClient.setQueryData(key, data)
            })
          }
        },
      }),
    )

    const destroy = useCallback(
      async (flagId: string) => mutateAsync(flagId),
      [mutateAsync],
    )

    return { destroy, isPending }
  }
}
